import { useMemo, useState } from 'react'
import type { PublicAttendee } from '@/lib/publicForm'

interface Props {
  attendees: PublicAttendee[]
  selected: Set<string>
  maxSelections: number
  onToggle: (attendeeId: string) => void
  /** The submitter's own attendee id, hidden from the list once known. */
  selfId?: string | undefined
}

/**
 * The list of people to choose from.
 *
 * Shows first name and nametag number only — that is all the server will return
 * (see `public_get_form_attendees`), and all an attendee needs: the number is
 * what they wrote down, the name is what they remember.
 *
 * Filtering matters more here than it would on a desktop screen. Fifteen dates
 * means fifteen or more rows, and scrolling a phone to find "104" is slower
 * than typing it.
 */
export function PickList({ attendees, selected, maxSelections, onToggle, selfId }: Props) {
  const [query, setQuery] = useState('')

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const withoutSelf = attendees.filter((a) => a.id !== selfId)
    if (needle === '') return withoutSelf

    return withoutSelf.filter(
      (a) => a.matchmaking_id.includes(needle) || a.first_name.toLowerCase().includes(needle)
    )
  }, [attendees, query, selfId])

  const atLimit = selected.size >= maxSelections

  return (
    <div className="stack">
      <div className="pick-search">
        <span className="pick-search-icon" aria-hidden="true">
          ⌕
        </span>
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
          }}
          placeholder="Search by name or number"
          aria-label="Search the list by name or number"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          enterKeyHint="search"
        />
      </div>

      {visible.length === 0 ? (
        <p className="field-hint">No one matches “{query}”.</p>
      ) : (
        <ul className="pick-list">
          {visible.map((attendee) => {
            const isSelected = selected.has(attendee.id)
            // Once the cap is reached, unselected rows are disabled rather than
            // silently ignoring the tap.
            const disabled = !isSelected && atLimit

            return (
              <li key={attendee.id}>
                <button
                  type="button"
                  className="pick"
                  aria-pressed={isSelected}
                  disabled={disabled}
                  onClick={() => {
                    onToggle(attendee.id)
                  }}
                >
                  <span className="pick-check" aria-hidden="true">
                    ✓
                  </span>
                  <span className="pick-name">{attendee.first_name}</span>
                  <span className="pick-number">{attendee.matchmaking_id}</span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
